import { t } from '@lingui/core/macro';
import { forwardRef, useMemo } from 'react';

import TooltipRow from '@/components/Flare/guides/TooltipRow';
import MultiSeriesTooltip from '@/components/MultiSeriesTooltip/MultiSeriesTooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';

import SimpleSankeyNodeLabel from './SimpleSankeyNodeLabel';
import { type SankeyNodeWithLinks } from './types';

const MIN_NODE_HEIGHT = 1;

type Props = {
  node: SankeyNodeWithLinks;
  maxLevel: number;
  textFilterId: string;
  hoveredNode?: SankeyNodeWithLinks;
  isTooltipOpen: boolean;
  isTooltipSticky: boolean;
  minHitboxHeight?: number;
  labelHorizontalPadding?: number;
  tabIndex?: number;
  onSelect?: (e: React.MouseEvent<SVGElement> | React.KeyboardEvent<SVGElement>) => void;
  onMouseEnter?: () => void;
  onMouseExit?: () => void;
  onStickyTooltipClose?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<SVGGElement>) => void;
};

const walkUp = (hoveredNode: SankeyNodeWithLinks, node?: SankeyNodeWithLinks): boolean => {
  if (!node) {
    return false;
  }
  if (node.id === hoveredNode.id) {
    return true;
  }
  return node.targetLinks?.some((link) => walkUp(hoveredNode, link.targetNode)) ?? false;
};

const walkDown = (hoveredNode: SankeyNodeWithLinks, node?: SankeyNodeWithLinks): boolean => {
  if (!node) {
    return false;
  }
  if (node.id === hoveredNode.id) {
    return true;
  }
  return walkDown(hoveredNode, node.sourceLink?.sourceNode) ?? false;
};

const SimpleSankeyNode = forwardRef<SVGGElement, Props>(
  (
    {
      node,
      maxLevel,
      textFilterId,
      hoveredNode,
      isTooltipOpen,
      isTooltipSticky,
      minHitboxHeight = 16,
      labelHorizontalPadding = 12,
      onSelect,
      onMouseEnter,
      onMouseExit,
      onStickyTooltipClose,
      tabIndex,
      onFocus,
      onKeyDown,
    },
    ref,
  ) => {
    // recursively walk up and down the tree to find the hovered node. If the hovered node is found,
    // return true, otherwise return false.
    const isEmphasized = useMemo(() => {
      if (!hoveredNode) {
        return true;
      }

      return walkUp(hoveredNode, node) || walkDown(hoveredNode, node);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hoveredNode?.id]);

    const nodeHeight = Math.max(MIN_NODE_HEIGHT, node.height);
    const tooltipSide = node.level === maxLevel ? 'right' : 'left';

    return (
      <Tooltip open={isTooltipOpen} disableHoverablePopup={!isTooltipSticky}>
        <TooltipTrigger
          render={
            <g
              key={node.id}
              ref={ref}
              className="outline-none"
              onClick={onSelect}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseExit}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
              tabIndex={tabIndex}
              role="option"
              aria-label={`${node.name}: ${node.value}`}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
            >
              {/* Visible node rect */}
              <rect
                className={cn('cursor-pointer transition-[fill-opacity] duration-500 ease-in-out')}
                x={node.x}
                y={node.y}
                width={node.width}
                height={nodeHeight}
                fill={node.color}
                fillOpacity={isEmphasized ? 1 : 0.25}
                pointerEvents="none"
              />
              {/* Invisible hitbox, expands to at least `minHitboxHeight` */}
              <rect
                x={node.x - labelHorizontalPadding}
                y={node.y - (Math.max(minHitboxHeight, nodeHeight) - nodeHeight) / 2}
                width={node.width + labelHorizontalPadding * 2}
                height={Math.max(minHitboxHeight, nodeHeight)}
                fill="transparent"
                pointerEvents="all"
              />
              <SimpleSankeyNodeLabel
                node={node}
                maxLevel={maxLevel}
                textFilterId={textFilterId}
                isEmphasized={isEmphasized}
                labelHorizontalPadding={labelHorizontalPadding}
              />
            </g>
          }
        />
        <TooltipContent
          side={tooltipSide}
          className="max-w-150 p-5"
          align={tooltipSide === 'right' ? 'start' : 'end'}
        >
          <MultiSeriesTooltip
            title={node.name}
            sticky={isTooltipSticky}
            onClose={onStickyTooltipClose}
          >
            {node.targetLinks?.map((link) => (
              <TooltipRow
                key={link.targetNode.id}
                color={link.targetNode.color}
                name={link.targetNode.name}
                value={numberFormat(link.value)}
              />
            ))}
            <TooltipRow name={t`Total`} value={numberFormat(node.value)} />
          </MultiSeriesTooltip>
        </TooltipContent>
      </Tooltip>
    );
  },
);

SimpleSankeyNode.displayName = 'SimpleSankeyNode';

export default SimpleSankeyNode;
